/**
 * セクション末尾に新規セクションを追加するボタン。
 * 仕様書: docs/01_spec/spec_mail_system.md B-14.2.3 (Phase 22a)
 *
 * クリック → セクション型を選択するメニュー → 選択でコールバック発火
 * Phase 22c で「セクション間ホバー隙間プラスボタン」も同じロジックを利用予定
 */
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { MailSectionType } from '@goenhub/shared';

interface Props {
  onAdd: (type: MailSectionType) => void;
}

const SECTION_OPTIONS: Array<{ type: MailSectionType; label: string; desc: string }> = [
  { type: 'greeting', label: '冒頭あいさつ', desc: '宛名・挨拶' },
  { type: 'lead', label: 'リード文', desc: '導入・フック' },
  { type: 'body', label: '本文', desc: '主たる内容' },
  { type: 'cta', label: 'CTA', desc: 'ボタン誘導' },
  { type: 'closing', label: '締め', desc: '署名・フッター' },
  { type: 'ps', label: '追伸', desc: '補足' },
];

export function AddSectionButton({ onAdd }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded border border-dashed border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[var(--accent-subtle)] transition-colors text-xs"
      >
        <Plus className="size-3.5" />
        セクションを追加
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1 z-20 rounded border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg overflow-hidden">
          {SECTION_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              onClick={() => {
                onAdd(opt.type);
                setOpen(false);
              }}
              className="w-full flex items-baseline gap-2 px-3 py-2 text-left hover:bg-[var(--bg-hover)] transition-colors"
            >
              <span className="text-[13px] font-medium text-[var(--text-primary)]">
                {opt.label}
              </span>
              <span className="text-[10px] text-[var(--text-muted)]">{opt.desc}</span>
              <code className="ml-auto text-[10px] font-mono text-[var(--text-muted)]">
                {opt.type}
              </code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
